import React, { useEffect, useRef, useState } from 'react';

import { FormattedMessage } from '../../util/reactIntl';
import { PrimaryButton } from '../Button/Button';

import css from './LineMessageFrequencySettings.module.css';

export const normalizeLineMessageThrottleEnabled = value => value === true;

const LineMessageFrequencySettings = props => {
  const { lineMessageThrottleEnabled, onSave } = props;
  const initialValue = normalizeLineMessageThrottleEnabled(lineMessageThrottleEnabled);
  const [savedValue, setSavedValue] = useState(initialValue);
  const [draftValue, setDraftValue] = useState(initialValue);
  const [saveInProgress, setSaveInProgress] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const savedValueRef = useRef(initialValue);
  const saveRequestInProgressRef = useRef(false);
  const isDirty = draftValue !== savedValue;

  useEffect(() => {
    const nextSavedValue = normalizeLineMessageThrottleEnabled(lineMessageThrottleEnabled);
    const previousSavedValue = savedValueRef.current;

    savedValueRef.current = nextSavedValue;
    setSavedValue(nextSavedValue);
    setDraftValue(currentValue =>
      currentValue === previousSavedValue ? nextSavedValue : currentValue
    );
  }, [lineMessageThrottleEnabled]);

  const handleSave = async () => {
    if (
      !isDirty ||
      saveInProgress ||
      saveRequestInProgressRef.current ||
      typeof onSave !== 'function'
    ) {
      return;
    }

    const valueToSave = draftValue;
    saveRequestInProgressRef.current = true;
    setSaveInProgress(true);
    setSaveError(null);

    try {
      await onSave(valueToSave);
      savedValueRef.current = valueToSave;
      setSavedValue(valueToSave);
    } catch (error) {
      setSaveError(error);
    } finally {
      saveRequestInProgressRef.current = false;
      setSaveInProgress(false);
    }
  };

  return (
    <section
      className={css.card}
      aria-labelledby="line-message-frequency-heading"
      data-testid="line-message-frequency-card"
    >
      <h2 id="line-message-frequency-heading" className={css.title}>
        <FormattedMessage id="LineMessageFrequencySettings.title" />
      </h2>
      <p className={css.description}>
        <FormattedMessage id="LineMessageFrequencySettings.description" />
      </p>

      <label className={css.checkboxRow}>
        <input
          className={css.checkbox}
          type="checkbox"
          checked={draftValue}
          disabled={saveInProgress}
          onChange={event => {
            setDraftValue(event.target.checked);
            setSaveError(null);
          }}
        />
        <span className={css.checkboxControl} aria-hidden="true" />
        <span className={css.checkboxCopy}>
          <span className={css.checkboxLabel}>
            <FormattedMessage id="LineMessageFrequencySettings.checkboxLabel" />
          </span>
          <span className={css.supportingText}>
            <FormattedMessage id="LineMessageFrequencySettings.supportingText" />
          </span>
        </span>
      </label>

      {saveError ? (
        <p className={css.error} role="alert">
          <FormattedMessage id="LineMessageFrequencySettings.saveFailed" />
        </p>
      ) : null}

      <div className={css.actions}>
        <PrimaryButton
          type="button"
          className={css.saveButton}
          inProgress={saveInProgress}
          disabled={!isDirty || saveInProgress}
          onClick={handleSave}
        >
          <FormattedMessage id="LineMessageFrequencySettings.save" />
        </PrimaryButton>
        {isDirty ? (
          <span className={css.unsaved} role="status">
            <FormattedMessage id="LineMessageFrequencySettings.unsavedChanges" />
          </span>
        ) : null}
      </div>
    </section>
  );
};

export default LineMessageFrequencySettings;
